import React, { useContext } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";
import { StringMapValidValue } from "../../types/StringMap";
import { A11yText } from "../A11yText";
import { ThemeContext } from "../Theme";

import styles from "./Asterisk.scss";

declare const validAsteriskMapValue: unique symbol;
export type AsteriskVariantMapValue = StringMapValidValue<
  typeof validAsteriskMapValue
>;

const AsteriskVariants = {
  currentColor: styles.currentColor as AsteriskVariantMapValue,
} as const;

export type AsteriskVariantMapKey = keyof AsteriskVariantMap;
export type AsteriskVariantMap = typeof AsteriskVariants;
export const AsteriskVariantValues = (Object.keys(
  AsteriskVariants
) as (keyof typeof AsteriskVariants)[]).map((key) => AsteriskVariants[key]);

type AsteriskBaseProps = {
  customClass?: string;
  variants?: AsteriskVariantMapValue | AsteriskVariantMapValue[];
};

export type AsteriskProps = AsteriskBaseProps &
  Omit<React.ComponentPropsWithoutRef<"button">, keyof AsteriskBaseProps>;

const defaultProps = {
  customClass: "",
  variants: undefined,
  children: undefined,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  AsteriskBaseProps & Pick<AsteriskProps, "children">,
  typeof defaultProps
> = defaultProps;

export const Asterisk: ((props: AsteriskProps) => JSX.Element) &
  ReactComponentStaticProps<AsteriskProps> & {
    Variant: AsteriskVariantMap;
  } = ({
  children,
  variants = defaultProps.variants,
  customClass = defaultProps.customClass,
  ...restProps
}: AsteriskProps): JSX.Element => {
  const theme = useContext(ThemeContext);
  return (
    <button
      type="button"
      className={cx(
        styles.asterisk,
        theme && styles[theme],
        variants,
        customClass
      )}
      {...restProps}
    >
      {children && <A11yText>{children}</A11yText>}
      <span className={styles.star} aria-hidden="true">
        *
      </span>
    </button>
  );
};

Asterisk.propTypes = {
  customClass: PropTypes.string,
  variants: PropTypes.oneOfType([
    PropTypes.oneOf(AsteriskVariantValues),
    PropTypes.arrayOf(PropTypes.oneOf(AsteriskVariantValues).isRequired),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
} as const;

Asterisk.defaultProps = validatedDefaultProps;

Asterisk.Variant = AsteriskVariants;

Asterisk.displayName = "Asterisk";

export default Asterisk;
